import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import classes from "./teamCard.module.scss";
import { ITeam, teamService } from "../../../../entities/team";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { TeamDetails } from "../teamDetails/TeamDetails";
import { initialFormState, ITeamFormState } from "../../lib/types";
import { useTeamsCommon } from "../../lib/useTeamsCommon";
import { TeamFormModal } from "../teamFormModal/TeamFormModal";
import { TeamDeleteModal } from "../teamDeleteModal/TeamDeleteModal";

export const TeamCardWidget: FC = () => {
    const navigate = useNavigate()
    const {id} = useParams<{id: string}>()
    const teamId = id || ''
    const [isLoading, setIsLoading] = useState(true)
    const [team, setTeam] = useState<ITeam | null>(null)
    const [teamModalOpen, setTeamModalOpen] = useState(false)
    const [editingTeamId, setEditingTeamId] = useState<string | null>(null)
    const [form, setForm] = useState<ITeamFormState>(initialFormState)
    const [formError, setFormError] = useState('')
    const [deletingTeam, setDeletingTeam] = useState<ITeam | null>(null)
    const {canUseTeamForm, handleError} = useTeamsCommon()

    const loadTeam = async () => {
        try{
            setIsLoading(true)
            const item = await teamService.get(teamId)
            setTeam(item)
        }
        catch(e){
            handleError(e, 'Ошибка при получении карточки звена')
            navigate('/teams', {replace: true})
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if(teamId){
            loadTeam()
        }
        else{
            navigate('/teams', {replace: true})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamId])

    const openEditModal = async (currentTeamId: string) => {
        try{
            const currentTeam = await teamService.get(currentTeamId)
            const tgAccounts = currentTeam.tgAccounts.map(item => ({id: Number(item.id), name: item.title || item.username}))
            const coordinator = currentTeam.coordinators[0]
                ? {id: Number(currentTeam.coordinators[0].userId), name: currentTeam.coordinators[0].fullName}
                : null
            const tgCuratorIds: Record<number, number[]> = {}

            currentTeam.tgCurators.forEach(item => {
                tgCuratorIds[Number(item.tgAccount.id)] = item.curators.map(curator => Number(curator.userId))
            })

            setEditingTeamId(currentTeam.id)
            setForm({
                name: currentTeam.name,
                tgAccounts,
                coordinator,
                tgCuratorIds,
            })
            setFormError('')
            setTeamModalOpen(true)
        }
        catch(e){
            handleError(e, 'Ошибка при подготовке формы редактирования звена')
        }
    }

    if(isLoading){
        return <section className={classes.panel}><section className={classes.empty}><LoaderSpinner /></section></section>
    }

    if(!team){
        return <section className={classes.panel}><section className={classes.empty}>Звено не найдено</section></section>
    }

    return (
        <>
            <TeamDetails
                team={team}
                canOpenEditor={canUseTeamForm}
                onEdit={openEditModal}
                onDelete={setDeletingTeam}
            />

            <TeamFormModal
                open={teamModalOpen}
                setOpen={(open) => {
                    setTeamModalOpen(open)
                    if(!open){
                        setEditingTeamId(null)
                        setForm(initialFormState)
                        setFormError('')
                    }
                }}
                editingTeamId={editingTeamId}
                form={form}
                setForm={setForm}
                formError={formError}
                setFormError={setFormError}
                onSaveSuccess={async (nextTeam) => {
                    setTeam(nextTeam)
                    await loadTeam()
                }}
            />
            <TeamDeleteModal
                team={deletingTeam}
                setTeam={setDeletingTeam}
                onDeleteSuccess={() => {
                    navigate('/teams', {replace: true})
                }}
            />
        </>
    )
}
